import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { PollingStatus } from '../../services/conversations.service';

@Component({
  selector: 'app-polling-status-indicator',
  templateUrl: './polling-status-indicator.component.html',
  styleUrls: ['./polling-status-indicator.component.scss']
})
export class PollingStatusIndicatorComponent implements OnInit, OnDestroy {
  @Input() pollingStatus$!: Observable<PollingStatus | null>;

  currentStatus: PollingStatus | null = null;
  private subscription?: Subscription;

  ngOnInit() {
    if (this.pollingStatus$) {
      this.subscription = this.pollingStatus$.subscribe(status => {
        this.currentStatus = status;
      });
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  get isPolling(): boolean {
    return this.currentStatus?.status === 'polling';
  }

  get isComplete(): boolean {
    return this.currentStatus?.status === 'complete';
  }

  get isTimeout(): boolean {
    return this.currentStatus?.status === 'timeout';
  }

  get isError(): boolean {
    return this.currentStatus?.status === 'error';
  }

  get totalElapsedSeconds(): number {
    return Math.floor((this.currentStatus?.totalElapsed || 0) / 1000);
  }

  get dynamicStatusText(): string {
    const elapsed = this.totalElapsedSeconds;

    if (elapsed < 10) {
      return 'Analyzing documents...';
    } else if (elapsed < 20) {
      return 'Extracting key concepts...';
    } else {
      return 'Generating final response...';
    }
  }

  get progressPercentage(): number {
    if (!this.currentStatus || this.currentStatus.status !== 'polling') {
      return 0;
    }

    const elapsed = this.totalElapsedSeconds;
    const typical = 30; // Typical response time in seconds

    // Calculate percentage based on elapsed vs typical time
    const percentage = Math.min(Math.floor((elapsed / typical) * 100), 95);

    // Don't show percentage until we have some elapsed time
    return elapsed >= 3 ? percentage : 0;
  }

  get estimatedTimeRemaining(): string {
    if (!this.currentStatus || this.currentStatus.status !== 'polling') {
      return '';
    }

    // Simple estimation based on current elapsed time and typical response times
    const elapsed = this.totalElapsedSeconds;
    const typical = 30; // Typical response time in seconds
    const remaining = Math.max(0, typical - elapsed);

    if (remaining > 60) {
      const minutes = Math.floor(remaining / 60);
      return `~${minutes}m remaining`;
    } else if (remaining > 0) {
      return `~${remaining}s remaining`;
    } else {
      return 'Processing...';
    }
  }

  formatElapsedTime(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    }
  }
}
